import { useEffect, useState } from 'react';
import type { CalcMethod } from '../types/CalcMethod';
import { getCalcMethods } from '../api/calcMethods';

// Module-level cache shared across the page's lifetime. Fetched LAZILY (first
// hook use); cleared on logout via clearCalcMethodsCache(). Mirrors useCrcies.
// Callers typically filter by calcTypeId (1=exposure, 2=valuation) client-side.
let cache: CalcMethod[] | null = null;
let inflight: Promise<CalcMethod[]> | null = null;

function load(): Promise<CalcMethod[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getCalcMethods()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearCalcMethodsCache(): void {
  cache = null;
  inflight = null;
}

interface UseCalcMethodsResult {
  data: CalcMethod[] | null;
  loading: boolean;
  error: string | null;
}

export function useCalcMethods(): UseCalcMethodsResult {
  const [state, setState] = useState<UseCalcMethodsResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }
    let cancelled = false;
    load()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
